// #1909 (docs/score/query-performance.md, tier 2) — ringsFor moved to
// score-common/src/utils/rings.js so score-api's reconcile pass computes a
// show's summary.rings with the SAME comparator this module used to keep
// privately. Re-exported here (rather than each call site importing
// "score-common" directly) so this file stays the one place callers reach
// for a ring order, and the module header below still describes the whole
// picture.
//
// Distinct `class.ring` values for a show's class list, sorted into a
// deterministic, natural-numeric order ("Ring 2" before "Ring 10").
// score-api has no ring-listing route (docs/score/announcer.md), ring is FVD
// free text, so the class list is the only source of what rings exist when a
// show's per-show summary row (#1909) is absent (not yet re-reconciled).
// `classList` comes from `useClassesForShow`, revalidated on every 5s entity
// tick with no ordering guarantee from the API. Sorting here (rather than
// trusting first-seen order) is what keeps the result identical across
// polls and reloads for every consumer (#1646).
// Used by AnnouncerContext (default-ring resolution) and OrderOfGo (#926
// arena tabs, ungated) so both stay scoped to the same show's rings, in the
// same order.
export { ringsFor } from "score-common";

// #1740 — ring is no longer a setup-time choice, it is AnnouncerContext's
// own default, resolved once a show's rings are known. #2112 — there is no
// setup screen at all any more; this now resolves the moment a show becomes
// active (App.jsx's ActiveShowProvider), including after a free show switch.
// Keep the persisted ring if it is still one of the show's current rings;
// otherwise fall back to the first entry of ringsFor's deterministic order.
// Returns null when there is nothing to resolve to yet (an empty
// `availableRings`, e.g. the class list hasn't loaded) — the caller is
// expected to leave the existing ring alone in that case, not clear it.
export function resolveDefaultRing(persistedRing, availableRings) {
  if (persistedRing && availableRings.includes(persistedRing)) return persistedRing;
  return availableRings.length ? availableRings[0] : null;
}

// #1968 — availableRings must never OMIT a ring the show's live class list
// carries, even though selectedShow.summary (#1909) is a snapshot frozen at
// show-selection time (see AnnouncerContext.jsx's #1968 note for the chosen
// approach and why). `liveRings` (ringsFor(classList), recomputed fresh every
// render off useClassesForShow's own cache-then-network read) is authoritative
// whenever it has anything at all — a mid-show ring addition shows up here as
// soon as THIS component's own class-list fetch next runs (on mount / showId
// change; unlike OrderOfGo's own instance, this one is NOT on a 5s poll, so
// it takes a reload, not the same round trip OrderOfGo's tabs take). Ahead
// of that, `summaryRings` is used ONLY as an early-paint seed for the brief
// window before the live class list has produced anything (or for a show
// whose class list is genuinely, persistently empty) — never once liveRings
// is non-empty, so a stale summary can no longer shadow a ring the class
// list already knows about.
export function availableRingsFor(liveRings, summaryRings) {
  if (liveRings.length) return liveRings;
  return summaryRings?.length ? summaryRings : [];
}
